package io.horion.cv.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import io.horion.cv.utils.Colors
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected

data class Language(
    val name: String,
    val level: LanguageLevel
)

enum class LanguageLevel {
    BEGINNER, INTERMEDIATE, ADVANCED, FLUENT, NATIVE;

    fun getDisplayName(): String {
        return when(this) {
            BEGINNER -> "Débutant"
            INTERMEDIATE -> "Intermédiaire"
            ADVANCED -> "Avancé"
            FLUENT -> "Courant"
            NATIVE -> "Natif"
        }
    }
}

@Composable
fun LanguagesManager(
    title: String = "Langues",
    languages: List<Language>,
    onLanguagesChange: (List<Language>) -> Unit,
    breakpoint: Breakpoint
) {
    val isMobile = breakpoint < Breakpoint.MD
    val levelOptions = LanguageLevel.values().toList()

    // États pour l'ajout d'une nouvelle langue
    var newLanguageName by remember { mutableStateOf("") }
    var newLanguageLevel by remember { mutableStateOf(LanguageLevel.BEGINNER) }
    var isAdding by remember { mutableStateOf(false) }

    // État pour l'édition
    var editingIndex by remember { mutableStateOf<Int?>(null) }
    var editingName by remember { mutableStateOf("") }
    var editingLevel by remember { mutableStateOf(LanguageLevel.BEGINNER) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 10.px)
    ) {
        // Titre
        SpanText(
            title,
            modifier = Modifier
                .fontSize(18.px)
                .fontWeight(FontWeight.Bold)
                .margin(bottom = 10.px)
        )

        // Box principale
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .border(1.px, LineStyle.Solid, Colors.LightGray)
                .borderRadius(10.px)
                .padding(15.px)
        ) {
            // Liste des langues existantes
            languages.forEachIndexed { index, language ->
                val isEditing = editingIndex == index

                if (isEditing) {
                    // Mode édition
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .margin(bottom = 10.px)
                    ) {
                        // Ligne pour le nom et niveau
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(10.px)
                        ) {
                            // Input pour le nom
                            Input(
                                type = InputType.Text,
                                attrs = Modifier
                                    .flexGrow(1)
                                    .padding(8.px)
                                    .borderRadius(6.px)
                                    .border(1.px, LineStyle.Solid, Colors.LightGray)
                                    .toAttrs {
                                        value(editingName)
                                        onInput { event ->
                                            editingName = event.target.asDynamic()?.value ?: ""
                                        }
                                    }
                            )

                            // Select pour le niveau
                            Select(
                                attrs = Modifier
                                    .width(if (isMobile) 120.px else 150.px)
                                    .padding(8.px)
                                    .borderRadius(6.px)
                                    .border(1.px, LineStyle.Solid, Colors.LightGray)
                                    .toAttrs {
                                        onChange { event ->
                                            val selectedIndex = event.target.asDynamic()?.selectedIndex
                                            if (selectedIndex != null && selectedIndex >= 0) {
                                                editingLevel = levelOptions[selectedIndex]
                                            }
                                        }
                                    }
                            ) {
                                levelOptions.forEach { option ->
                                    Option(
                                        value = option.getDisplayName(),
                                        attrs = Modifier.toAttrs {
                                            if (editingLevel == option) {
                                                selected()
                                            }
                                        }
                                    ) {
                                        SpanText(option.getDisplayName())
                                    }
                                }
                            }
                        }

                        // Boutons en dessous
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .margin(top = 10.px),
                            horizontalArrangement = Arrangement.spacedBy(10.px)
                        ) {
                            Button(
                                onClick = {
                                    if (editingName.isNotBlank()) {
                                        val updatedLanguages = languages.toMutableList()
                                        updatedLanguages[index] = Language(editingName, editingLevel)
                                        onLanguagesChange(updatedLanguages)
                                        editingIndex = null
                                    }
                                },
                                modifier = Modifier.flexGrow(1)
                            ) {
                                SpanText("Sauvegarder")
                            }

                            Button(
                                onClick = {
                                    editingIndex = null
                                },
                                modifier = Modifier.flexGrow(1)
                            ) {
                                SpanText(" Annuler")
                            }
                        }
                    }
                } else {
                    // Mode affichage normal
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .margin(bottom = 10.px),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        // Nom et niveau
                        Row(
                            modifier = Modifier.flexGrow(1),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(10.px)
                        ) {
                            SpanText(
                                language.name,
                                modifier = Modifier
                                    .fontSize(18.px)
                                    .fontWeight(FontWeight.Bold)
                                    .width(if (isMobile) 100.px else 120.px)
                            )

                            SpanText(
                                language.level.getDisplayName(),
                                modifier = Modifier
                                    .fontSize(16.px)
                                    //.color(Colors.Primary)
                                    .borderRadius(6.px)
                                    .border(1.px, LineStyle.Solid, Colors.LightGray)
                                    .padding(4.px, 8.px)
                            )
                        }

                        // Boutons d'action
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.px)
                        ) {
                            Button(
                                modifier = Modifier
                                    .padding(topBottom = 5.px, leftRight = 10.px),
                                onClick = {
                                    editingIndex = index
                                    editingName = language.name
                                    editingLevel = language.level
                                }
                            ) {
                                SpanText("Éditer")
                            }

                            Button(
                                modifier = Modifier
                                    .padding(topBottom = 5.px, leftRight = 10.px),
                                onClick = {
                                    val updatedLanguages = languages.toMutableList()
                                    updatedLanguages.removeAt(index)
                                    onLanguagesChange(updatedLanguages)
                                }
                            ) {
                                SpanText(" Supprimer")
                            }
                        }
                    }
                    //SEPARATEUR
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(1.px)
                            .backgroundColor(Colors.LightGray)
                            .margin(topBottom = 15.px)
                    )
                }
            }

            // Formulaire d'ajout
            if (isAdding) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .margin(top = 10.px)
                ) {
                    // Ligne pour le nom et niveau
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(10.px)
                    ) {
                        // Input pour le nom
                        Input(
                            type = InputType.Text,
                            attrs = Modifier
                                .flexGrow(1)
                                .padding(8.px)
                                .borderRadius(6.px)
                                .border(1.px, LineStyle.Solid, Colors.LightGray)
                                .toAttrs {
                                    placeholder("Nouvelle langue")
                                    value(newLanguageName)
                                    onInput { event ->
                                        newLanguageName = event.target.asDynamic()?.value ?: ""
                                    }
                                }
                        )

                        // Select pour le niveau
                        Select(
                            attrs = Modifier
                                .width(if (isMobile) 120.px else 150.px)
                                .padding(8.px)
                                .borderRadius(6.px)
                                .border(1.px, LineStyle.Solid, Colors.LightGray)
                                .toAttrs {
                                    onChange { event ->
                                        val selectedIndex = event.target.asDynamic()?.selectedIndex
                                        if (selectedIndex != null && selectedIndex >= 0) {
                                            newLanguageLevel = levelOptions[selectedIndex]
                                        }
                                    }
                                }
                        ) {
                            levelOptions.forEach { option ->
                                Option(value = option.getDisplayName()) {
                                    SpanText(option.getDisplayName())
                                }
                            }
                        }
                    }

                    // Boutons en dessous
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .margin(top = 10.px),
                        horizontalArrangement = Arrangement.spacedBy(10.px),
                    ) {
                        Button(
                            onClick = {
                                if (newLanguageName.isNotBlank()) {
                                    val newLanguage = Language(newLanguageName, newLanguageLevel)
                                    onLanguagesChange(languages + newLanguage)
                                    newLanguageName = ""
                                    newLanguageLevel = LanguageLevel.BEGINNER
                                    isAdding = false
                                }
                            },
                            modifier = Modifier.flexGrow(1)
                        ) {
                            SpanText(" Ajouter")
                        }

                        Button(
                            onClick = {
                                isAdding = false
                                newLanguageName = ""
                            },
                            modifier = Modifier.flexGrow(1)
                        ) {
                            SpanText(" Annuler")
                        }
                    }
                }
            }

            // Bouton pour ajouter une nouvelle langue
            if (!isAdding) {
                Button(
                    onClick = { isAdding = true },
                    modifier = Modifier
                        .fillMaxWidth()
                        .margin(top = 10.px)
                ) {
                    SpanText("+ Ajouter une langue")
                }
            }
        }
    }
}